import * as React from "react";
import { FormGroup } from "@material-ui/core";
import { $, merge } from "glamor";
import { CheckboxListBody } from "./checkbox-list-body";
import { CheckboxListHeader } from "./checkbox-list-header";
import { ISize } from "../../interfaces/dropdown-sizes-types";
import theme from "styles/theme";
import { BLANK } from "./checkbox-list-new-item";

interface ICheckboxListProps {
    items: ISize[];
    onChangeItems: (newItems: ISize[]) => void;
    onItemAdded: () => void;
    onlyManualSizes?: boolean;
}

export const CheckboxList: React.FC<ICheckboxListProps> = ({
    items,
    onChangeItems,
    onItemAdded,
    onlyManualSizes = false,
}) => {
    const styles = React.useMemo(
        () =>
            merge(
                $(" hr", {
                    width: "100%",
                    border: `0.5px solid ${theme.palette.lighterGrey}`,
                }),
                $(" .MuiFormControlLabel-root", {
                    margin: "0",
                }),
                $(" .MuiTypography-body1", {
                    fontSize: "1.4rem",
                })
            ),
        []
    );

    const areAllSelected = React.useMemo(() => items.every((item) => item.selected), [items]);
    const isIndeterminate = React.useMemo(() => items.some((item) => item.selected), [items]);

    const handleChange = React.useCallback(
        (item: ISize, createNewItem = false) => {
            const currentItems = [...items];

            const itemIndexToUpdate = currentItems.findIndex((currentItem) => currentItem.id === item.id);

            if (itemIndexToUpdate > -1) {
                currentItems[itemIndexToUpdate] = { ...item, id: item.title };

                if (createNewItem) {
                    currentItems.push({
                        id: BLANK,
                        title: BLANK,
                        selected: true,
                        wasAdded: true,
                        editable: true,
                    });
                }
                onChangeItems(currentItems);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items]
    );
    const handleToggleSelect = React.useCallback(
        (allSelected: boolean) => () => {
            const itemsToUpdate = items.map((currentItem) => ({
                ...currentItem,
                selected: !allSelected,
            }));

            onChangeItems(itemsToUpdate);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items]
    );

    return (
        <FormGroup {...styles}>
            {!onlyManualSizes && (
                <>
                    <CheckboxListHeader
                        areAllSelected={areAllSelected}
                        isIndeterminate={isIndeterminate}
                        onToggle={handleToggleSelect}
                    />
                    <hr />
                </>
            )}
            <CheckboxListBody items={items} onChange={handleChange} onItemAdded={onItemAdded} />
        </FormGroup>
    );
};
